import { useState, useRef, useCallback, useEffect, createContext, useContext } from 'react';
import { BadgeCheck, Play, Pause, ChevronDown } from 'lucide-react';

// ─── Video Playback Context (only one video plays at a time) ────────────────

interface VideoContextType {
  activeVideoId: string | null;
  setActiveVideoId: (id: string | null) => void;
}

const VideoContext = createContext<VideoContextType>({
  activeVideoId: null,
  setActiveVideoId: () => {},
});

// ─── Types ───────────────────────────────────────────────────────────────────

interface VideoTestimonial {
  type: 'video';
  id: string;
  name: string;
  role: string;
  videoSrc: string;
  posterSrc: string;
}

interface TweetTestimonial {
  type: 'tweet';
  id: string;
  name: string;
  role: string;
  avatarSrc: string;
  quote: string;
  date: string;
}

type TestimonialItem = VideoTestimonial | TweetTestimonial;

// ─── Data (ordered to match the masonry column layout from screenshot) ───────

const column1: TestimonialItem[] = [
  {
    type: 'video',
    id: 'andres',
    name: 'Andres',
    role: 'Director of Partnership @MoonPay',
    videoSrc: 'https://cdn.shopify.com/videos/c/o/v/c9713ad3b3ec42cdb0ea7fbd60c31286.mp4',
    posterSrc: 'https://ryder.id/cdn/shop/files/MoonPay.jpg?v=1770280163',
  },
  {
    type: 'tweet',
    id: 'tim-draper',
    name: 'Tim Draper',
    role: 'Founder @Draper Associates',
    avatarSrc: 'https://ryder.id/cdn/shop/files/tim-draper.jpg?v=1770282579',
    quote: 'Ryder is making the simplest secure hard wallet in case you want total control over your Bitcoin and other cryptocurrencies.',
    date: '6:12 AM · Dec 06, 2025',
  },
  {
    type: 'tweet',
    id: 'yohan-lee',
    name: 'Yohan Lee',
    role: 'CMO @FactBlock',
    avatarSrc: 'https://ryder.id/cdn/shop/files/yohanlee.jpg?v=1770370941',
    quote: 'My favorite hardware wallet!',
    date: '1:01 AM · Mar 11, 2025',
  },
];

const column2: TestimonialItem[] = [
  {
    type: 'tweet',
    id: 'sean-carey',
    name: 'Sean Carey',
    role: 'CTO @Helium',
    avatarSrc: 'https://ryder.id/cdn/shop/files/Sean_Carey-modified.png?v=1737981370',
    quote: "I am happy to say that Ryder is not vapor. Best hardware wallet experience I've ever had.",
    date: '1:38 AM · Aug 13, 2025',
  },
  {
    type: 'video',
    id: 'robinson',
    name: 'Robinson',
    role: 'Co-Founder @Wormhole',
    videoSrc: 'https://cdn.shopify.com/videos/c/o/v/6a45000a3233419cbe70723b56b6e006.mp4',
    posterSrc: 'https://ryder.id/cdn/shop/files/Wormhole.jpg?v=1770280028',
  },
  {
    type: 'tweet',
    id: 'phil-kwok',
    name: 'Phil Kwok',
    role: 'Co-Founder @EasyA',
    avatarSrc: 'https://ryder.id/cdn/shop/files/phil.jpg?v=1770372014',
    quote: "one of the coolest pieces of hardware i've ever seen.",
    date: '7:09 AM · Oct 18, 2025',
  },
];

const column3: TestimonialItem[] = [
  {
    type: 'video',
    id: 'dayana',
    name: 'Dayana',
    role: 'Media Lead @WalletConnect',
    videoSrc: 'https://cdn.shopify.com/videos/c/o/v/9b33543085714f589ae6554d544e4d1a.mp4',
    posterSrc: 'https://ryder.id/cdn/shop/files/WalletConnect.jpg?v=1770279834',
  },
  {
    type: 'tweet',
    id: 'austin-federa',
    name: 'Austin Federa',
    role: 'Strategy @Solana Foundation',
    avatarSrc: 'https://ryder.id/cdn/shop/files/images-modified.png?v=1737980905',
    quote: 'We need more new ideas for self-custody. I ordered a Ryder One hardware wallet with day one support on Solana.',
    date: '3:28 PM · Jan 12, 2026',
  },
];

const column4: TestimonialItem[] = [
  {
    type: 'tweet',
    id: 'joe-mezzani',
    name: 'Joe Mezzani',
    role: 'CEO @LunarCrush',
    avatarSrc: 'https://ryder.id/cdn/shop/files/joe-vezzani-ryder-testimonial.png?v=1737980075',
    quote: 'Ryder is a paradigm shift in experience which will change the way you feel about hardware wallets forever.',
    date: '2:03 PM · Jan 08, 2026',
  },
  {
    type: 'video',
    id: 'igor',
    name: 'Igor',
    role: 'Chief Growth Officer @Hacken',
    videoSrc: 'https://cdn.shopify.com/videos/c/o/v/51c67a37d03043a0b361f41ded735467.mp4',
    posterSrc: 'https://ryder.id/cdn/shop/files/Hacken.jpg?v=1770280271',
  },
];

// All items flat for mobile scroll
const allItems: TestimonialItem[] = [...column1, ...column2, ...column3, ...column4];

// ─── Lazy Video Hook ────────────────────────────────────────────────────────

function useLazyLoad(rootMargin = '200px') {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect(); // Once visible, stop observing
        }
      },
      { rootMargin, threshold: 0 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [rootMargin]);

  return { ref, isVisible };
}

// ─── Video Card ──────────────────────────────────────────────────────────────

function VideoCard({ item }: { item: VideoTestimonial }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const { activeVideoId, setActiveVideoId } = useContext(VideoContext);
  const { ref: lazyRef, isVisible } = useLazyLoad('300px');

  // Stop this video if another video becomes active
  useEffect(() => {
    if (activeVideoId !== item.id && isPlaying) {
      const video = videoRef.current;
      if (video) {
        video.pause();
        setIsPlaying(false);
      }
    }
  }, [activeVideoId, item.id, isPlaying]);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (isPlaying) {
      video.pause();
      setIsPlaying(false);
      setActiveVideoId(null);
    } else {
      // This will trigger the useEffect in other VideoCards to pause them
      setActiveVideoId(item.id);
      video.play();
      setIsPlaying(true);
    }
  }, [isPlaying, item.id, setActiveVideoId]);

  return (
    <div
      ref={lazyRef}
      className="relative rounded-lg sm:rounded-xl overflow-hidden cursor-pointer group bg-black"
      style={{ aspectRatio: '3/4' }}
      onClick={togglePlay}
    >
      {isVisible ? (
        <video
          ref={videoRef}
          src={item.videoSrc}
          poster={item.posterSrc}
          playsInline
          preload="none"
          className="w-full h-full object-cover"
          onEnded={() => {
            setIsPlaying(false);
            setActiveVideoId(null);
          }}
        />
      ) : (
        /* Poster placeholder before lazy load triggers */
        <img
          src={item.posterSrc}
          alt={item.name}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      )}
      {/* Play/Pause button overlay */}
      <div
        className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${
          isPlaying ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'
        }`}
      >
        <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center border border-white/20 transition-transform duration-200 group-hover:scale-110">
          {isPlaying ? (
            <Pause className="w-5 h-5 sm:w-6 sm:h-6 text-white fill-white" />
          ) : (
            <Play className="w-5 h-5 sm:w-6 sm:h-6 text-white fill-white ml-0.5" />
          )}
        </div>
      </div>
      {/* Bottom gradient + info */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent p-3 sm:p-4 pt-12">
        <p className="text-white font-semibold text-sm sm:text-base">{item.name}</p>
        <p className="text-white/70 text-[11px] sm:text-xs">{item.role}</p>
      </div>
    </div>
  );
}

// ─── Tweet Card ──────────────────────────────────────────────────────────────

function TweetCard({ item }: { item: TweetTestimonial }) {
  return (
    <div className="bg-white rounded-md sm:rounded-xl border border-gray-100 p-3.5 sm:p-4 flex flex-col justify-between shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-[0_2px_8px_rgba(0,0,0,0.08)] transition-shadow duration-200">
      {/* Header */}
      <div>
        <div className="flex items-start justify-between mb-2.5">
          <div className="flex items-center gap-2">
            <img
              src={item.avatarSrc}
              alt={item.name}
              className="w-9 h-9 rounded-full object-cover flex-shrink-0"
              loading="lazy"
            />
            <div>
              <div className="flex items-center gap-1">
                <span className="font-semibold text-[13px] text-gray-900">{item.name}</span>
                <BadgeCheck className="w-3.5 h-3.5 text-[#1d9bf0] flex-shrink-0" />
              </div>
              <p className="text-[11px] text-gray-500 leading-tight">{item.role}</p>
            </div>
          </div>
          {/* X (Twitter) logo */}
          <svg viewBox="0 0 24 24" className="w-4 h-4 text-gray-300 flex-shrink-0 mt-0.5" fill="currentColor">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
          </svg>
        </div>
        {/* Quote */}
        <p className="text-[13px] text-gray-800 leading-relaxed">{item.quote}</p>
      </div>
      {/* Date */}
      <p className="text-[11px] text-gray-400 mt-2.5">{item.date}</p>
    </div>
  );
}

// ─── Column Renderer ─────────────────────────────────────────────────────────

function MasonryColumn({ items }: { items: TestimonialItem[] }) {
  return (
    <div className="flex flex-col gap-3 sm:gap-4">
      {items.map((item) => (
        <div key={item.id}>
          {item.type === 'video' ? (
            <VideoCard item={item} />
          ) : (
            <TweetCard item={item} />
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function LovedByThousands() {
  const [activeVideoId, setActiveVideoId] = useState<string | null>(null);

  const scrollToReviews = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const HEADER_OFFSET = 100;
    const SETTLE_TIMEOUT = 3000; // Stop correcting after 3s

    const reviewsSection = document.getElementById('reviews-section');
    if (!reviewsSection) return;

    // Helper: scroll to the element's current position
    const doScroll = (smooth: boolean) => {
      const rect = reviewsSection.getBoundingClientRect();
      const absoluteTop = rect.top + window.scrollY;
      window.scrollTo({ top: absoluteTop - HEADER_OFFSET, behavior: smooth ? 'smooth' : 'auto' });
    };

    // Initial smooth scroll
    doScroll(true);

    // Use a ResizeObserver on <body> to detect layout shifts from lazy content
    // and keep correcting the scroll position until things settle
    let correctionTimer: ReturnType<typeof setTimeout>;
    let settled = false;

    const resizeObserver = new ResizeObserver(() => {
      if (settled) return;
      // Re-scroll instantly (no smooth) to correct for layout shift
      doScroll(false);
    });

    // Observe the document body for any size changes
    resizeObserver.observe(document.body);

    // Also do a few explicit correction passes for images that load without
    // triggering a body resize (e.g., images with explicit width/height)
    const corrections = [200, 500, 1000, 1500, 2000];
    const correctionTimers = corrections.map(delay =>
      setTimeout(() => {
        if (!settled) doScroll(false);
      }, delay)
    );

    // Stop all correction after SETTLE_TIMEOUT
    correctionTimer = setTimeout(() => {
      settled = true;
      resizeObserver.disconnect();
      correctionTimers.forEach(clearTimeout);
    }, SETTLE_TIMEOUT);

    // Cleanup if the component unmounts (unlikely but safe)
    return () => {
      settled = true;
      resizeObserver.disconnect();
      clearTimeout(correctionTimer);
      correctionTimers.forEach(clearTimeout);
    };
  }, []);

  return (
    <VideoContext.Provider value={{ activeVideoId, setActiveVideoId }}>
      <section id="testimonials" className="py-14 sm:py-20" style={{ backgroundColor: '#f5f5f5' }}>
        <div className="container px-4">
          {/* Header */}
          <div className="flex items-start sm:items-center justify-between mb-8 sm:mb-12">
            <div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900">
                Loved by thousands
              </h2>
              <p className="text-gray-500 mt-1.5 text-sm sm:text-base">
                From crypto enthusiasts like you
              </p>
            </div>
            <a
              href="#reviews-section"
              onClick={scrollToReviews}
              className="hidden sm:flex items-center gap-2 bg-[#6a63f5] text-white text-sm font-medium px-5 py-2.5 rounded-full hover:bg-[#5b54e6] transition-colors flex-shrink-0"
            >
              View more reviews
              <ChevronDown className="w-4 h-4" />
            </a>
          </div>

          {/* ── Desktop Masonry: 4 explicit columns ── */}
          <div className="hidden md:grid grid-cols-4 gap-3 lg:gap-4">
            <MasonryColumn items={column1} />
            <MasonryColumn items={column2} />
            <MasonryColumn items={column3} />
            <MasonryColumn items={column4} />
          </div>

          {/* ── Mobile: horizontal scroll ── */}
          <div className="md:hidden -mx-4">
            <div
              className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-4 px-4"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
            >
              {allItems.map((item) => (
                <div
                  key={item.id}
                  className="snap-start flex-shrink-0"
                  style={{ width: item.type === 'video' ? '200px' : '240px' }}
                >
                  {item.type === 'video' ? (
                    <VideoCard item={item} />
                  ) : (
                    <TweetCard item={item} />
                  )}
                </div>
              ))}

            </div>
            {/* Mobile CTA */}
            <div className="mt-6 flex justify-center">
              <a
                href="#reviews-section"
                onClick={scrollToReviews}
                className="flex items-center gap-2 bg-[#6a63f5] text-white text-sm font-medium px-5 py-2.5 rounded-full hover:bg-[#5b54e6] transition-colors"
              >
                View more reviews
                <ChevronDown className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </VideoContext.Provider>
  );
}
